// VoiceNote — record a ≤ 30 s handover voice note (MediaRecorder) → upload → whisper transcript; and a bearer-fetched player.
import React, { useEffect, useRef, useState } from 'react';
import { Mic, Square, Trash2, Loader2, Volume2 } from 'lucide-react';
import { apiCall, uploadFile, API_URL, getToken } from './_shared';

const MAX_SECONDS = 30;

export const VoiceNotePlayer = ({ shiftId, note, compact = false }) => {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    fetch(`${API_URL}/api/admin/shifts/${shiftId}/voice-note.audio`, { credentials: 'include', headers: { Authorization: `Bearer ${getToken()}` } })
      .then((r) => (r.ok ? r.blob() : null)).then((b) => { if (b) { url = URL.createObjectURL(b); setSrc(url); } }).catch(() => {});
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [shiftId, note?.at]);
  if (!note) return null;
  return (
    <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-2 text-sm space-y-1" data-testid="voice-note-player">
      <div className="flex items-center gap-2 text-xs text-indigo-900"><Volume2 className="w-3.5 h-3.5" /> Voice note · {note.duration_s ? `${note.duration_s} s` : ''} · {note.by}{note.language ? ` · ${note.language}` : ''}</div>
      {src ? <audio controls src={src} className="w-full h-8" data-testid="voice-note-audio" /> : <div className="text-xs text-slate-500">Loading audio…</div>}
      {!compact && (note.transcript ? <div className="text-slate-800 whitespace-pre-wrap" data-testid="voice-note-transcript">“{note.transcript}”</div>
        : <div className="text-xs text-slate-500 italic" data-testid="voice-note-transcript">Transcript unavailable{note.error ? ` — ${note.error}` : ''}</div>)}
    </div>
  );
};

const pickMime = () => ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg'].find((m) => window.MediaRecorder && MediaRecorder.isTypeSupported(m)) || '';

export const VoiceNoteRecorder = ({ shiftId, note, onChange }) => {
  const [state, setState] = useState(note ? 'saved' : 'idle'); // idle | recording | uploading | saved
  const [secs, setSecs] = useState(0);
  const [err, setErr] = useState('');
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const startedRef = useRef(0);

  useEffect(() => () => { clearInterval(timerRef.current); recRef.current?.stream?.getTracks().forEach((t) => t.stop()); }, []);

  const upload = async (blob, duration) => {
    setState('uploading'); setErr('');
    try {
      const ext = blob.type.includes('mp4') ? 'm4a' : blob.type.includes('ogg') ? 'ogg' : 'webm';
      const file = new File([blob], `handover.${ext}`, { type: blob.type.split(';')[0] });
      const r = await uploadFile(`/api/admin/shifts/${shiftId}/voice-note`, file, 'file', { duration });
      onChange(r.voice_note); setState('saved');
    } catch (e) { setErr(e.message); setState('idle'); }
  };

  const start = async () => {
    setErr('');
    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) { setErr('Voice recording is not supported in this browser.'); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = pickMime();
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        clearInterval(timerRef.current);
        const duration = Math.round((Date.now() - startedRef.current) / 100) / 10;
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || mime || 'audio/webm' });
        if (blob.size < 1000 || duration < 1) { setErr('Recording too short.'); setState('idle'); return; }
        upload(blob, duration);
      };
      recRef.current = rec; startedRef.current = Date.now(); setSecs(0); setState('recording');
      rec.start(250);
      timerRef.current = setInterval(() => {
        const s = Math.round((Date.now() - startedRef.current) / 1000); setSecs(s);
        if (s >= MAX_SECONDS && rec.state === 'recording') rec.stop();
      }, 250);
    } catch (e) { setErr(e.name === 'NotAllowedError' ? 'Microphone access was denied.' : e.message); }
  };
  const stop = () => { if (recRef.current?.state === 'recording') recRef.current.stop(); };
  const remove = async () => { try { await apiCall(`/api/admin/shifts/${shiftId}/voice-note`, { method: 'DELETE' }); onChange(null); setState('idle'); } catch (e) { setErr(e.message); } };

  return (
    <div className="space-y-2" data-testid="voice-note-recorder">
      <div className="flex flex-wrap items-center gap-2">
        {state === 'recording' ? (
          <button type="button" onClick={stop} className="inline-flex items-center gap-2 bg-red-600 text-white rounded-lg px-3 py-1.5 text-sm font-semibold" data-testid="voice-note-stop"><Square className="w-3.5 h-3.5" /> Stop · {secs}s / {MAX_SECONDS}s</button>
        ) : state === 'uploading' ? (
          <span className="inline-flex items-center gap-2 text-sm text-slate-600" data-testid="voice-note-uploading"><Loader2 className="w-4 h-4 animate-spin" /> Uploading & transcribing…</span>
        ) : (
          <button type="button" onClick={start} className="inline-flex items-center gap-2 border border-indigo-300 text-indigo-800 bg-white rounded-lg px-3 py-1.5 text-sm font-semibold" data-testid="voice-note-record"><Mic className="w-4 h-4" /> {note ? 'Re-record voice note' : 'Record voice note (max 30 s)'}</button>
        )}
        {note && state !== 'recording' && <button type="button" onClick={remove} className="inline-flex items-center gap-1 text-xs text-red-700" data-testid="voice-note-delete"><Trash2 className="w-3 h-3" /> Remove</button>}
        {state === 'recording' && <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />}
      </div>
      {err && <div className="text-xs text-red-700" data-testid="voice-note-error">{err}</div>}
      {note && state !== 'recording' && <VoiceNotePlayer shiftId={shiftId} note={note} />}
    </div>
  );
};

export default VoiceNoteRecorder;
